import { useEffect, type ReactNode } from 'react'
import { SOCKET } from '../../constants/socket'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { clearUserStatuses } from '../../redux/reducers/messenger/userStatusSlice'
import { socket } from '../../services/socket-setup'
import { acquireSocket, releaseSocket } from '../../utils/sharedSocket'
import { AUTH_RETRY_MS, shouldRetryConnect } from '../../utils/socketRetry'
import usePresenceReporter, { currentAppState } from '../hooks/usePresenceReporter'
import { useSocketHandler } from '../hooks/useSocketHandler'
interface SocketProviderProps {
  children: ReactNode
}

// The user the shared socket was opened for. The server fixes a socket's user
// at the handshake, so another user needs a new connection.
let socketUserId: string | null = null

const SocketProvider = ({ children }: SocketProviderProps) => {
  const { user, token } = useAppSelector((store) => store.auth)
  const dispatch = useAppDispatch()

  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    if (user && token) {
      // Opened for someone else (the account changed without a sign-out).
      if (socketUserId !== null && socketUserId !== String(user.id)) {
        socket.disconnect()
      }
      socketUserId = String(user.id)
      if (!socket.connected) {
        socket.connect()
      }

      // join-room answers with everyone's status, so no separate request.
      // Report the tab's visibility first: join-room starts counting the socket
      // and an unreported one counts as visible, so a hidden tab would show its
      // user Online for a moment on every (re)connect.
      const handleConnect = () => {
        socket.emit(SOCKET.Emitters.Presence_Update, { appState: currentAppState() })
        socket.emit(SOCKET.Emitters.Join_Room, user.id)
        socket.emit(SOCKET.Emitters.Set_Online)
      }

      // While there is no live connection, statuses can't be kept current, so
      // drop them: showing no dot beats showing a stale "Online". join-room
      // sends them all again on (re)connect.
      const handleConnectError = (error: Error) => {
        console.error('Socket connection failed:', error)
        dispatch(clearUserStatuses())
        if (shouldRetryConnect(error, socket.active)) {
          if (retryTimer) clearTimeout(retryTimer)
          retryTimer = setTimeout(() => {
            retryTimer = null
            if (!socket.connected) socket.connect()
          }, AUTH_RETRY_MS)
        }
      }

      const handleDisconnect = (reason: string) => {
        console.log('Socket disconnected:', reason)
        dispatch(clearUserStatuses())
      }

      socket.on('connect', handleConnect)
      socket.on('connect_error', handleConnectError)
      socket.on('disconnect', handleDisconnect)

      if (socket.connected) handleConnect()

      return () => {
        if (retryTimer) clearTimeout(retryTimer)
        try {
          socket.off('connect', handleConnect)
          socket.off('connect_error', handleConnectError)
          socket.off('disconnect', handleDisconnect)
        } catch (error) {
          console.warn('Socket cleanup warning:', error)
        }
      }
    } else {
      // Even when not connected: a socket that is still reconnecting would come
      // back with the token logout leaves in storage, signed in as that user.
      try {
        socket.disconnect()
      } catch (error) {
        console.warn('Socket disconnect warning:', error)
      }
      socketUserId = null
      // Signed out: the disconnect handler above is not attached, and the store
      // outlives this element, so drop the statuses here.
      dispatch(clearUserStatuses())
    }
  }, [user, user?.id, token, dispatch])

  // Cleanup on unmount
  useEffect(() => {
    acquireSocket()
    return () => {
      // Another chat element on the page (e.g. on /chat/) still uses the socket.
      if (!releaseSocket()) return
      // Even when not connected, as on sign-out above.
      try {
        socket.disconnect()
      } catch (error) {
        console.warn('Socket unmount cleanup warning:', error)
      }
      socketUserId = null
      // The disconnect handler is already detached by now (effects clean up in
      // order), and the store outlives this element: without this, the next
      // chat element would show this session's statuses until it reconnects.
      dispatch(clearUserStatuses())
    }
  }, [dispatch])

  useSocketHandler()
  usePresenceReporter()

  return <>{children}</>
}

export default SocketProvider
